<template>
  <div class="menu">
    <div
      class="menu__languages-mobile"
      role="navigation"
      aria-label="Language selection">
      <nuxt-link
        v-for="lang in languageLinks"
        :key="`mobile-${lang.code}-${locale}-${route.path}`"
        :to="lang.path || '#'"
        :class="{ 'menu__languages-mobile__active': lang.isActive }"
        :aria-current="lang.isActive ? 'page' : undefined"
        @click="(event) => handleLocaleLinkClick(event, lang.code)">
        {{ lang.code?.toUpperCase() }}
      </nuxt-link>
    </div>

    <div class="menu__column menu__left">
      <div class="menu__left__top">
        <span>{{ $t("menu.collections") }}</span>

        <div class="menu__left__top__titles">
          <nuxt-link
            :to="localePath('kitchens')"
            @click="handleLinkClick">
            <h1>{{ $t("menu.kitchens") }}</h1>
          </nuxt-link>
          <nuxt-link
            :to="localePath('living')"
            @click="handleLinkClick">
            <h1>{{ $t("menu.living") }}</h1>
          </nuxt-link>
          <nuxt-link
            :to="localePath('complements')"
            @click="handleLinkClick">
            <h1>{{ $t("menu.complements") }}</h1>
          </nuxt-link>
        </div>
      </div>

      <div class="menu__left-bottom">
        <menu-card @click="handleMenuCardClick" />
      </div>
    </div>

    <div class="menu__column menu__right menu__right__1">
      <div
        v-for="section in firstColumnSections"
        :key="section.id"
        class="menu__right__section">
        <span
          :id="`menu-section-title-${section.id}`"
          class="menu__right__section__title">
          {{ section.title }}
        </span>

        <button
          :id="`menu-toggle-${section.id}`"
          class="menu__right__section__header"
          type="button"
          :aria-expanded="expandedSections[section.id]"
          :aria-controls="`menu-panel-${section.id}`"
          @click="toggleSection(section.id)">
          <div
            class="menu__right__section__icon"
            aria-hidden="true">
            <span class="menu__right__section__icon-horizontal"></span>
            <span
              class="menu__right__section__icon-vertical"
              :class="{
                'menu__right__section__icon-vertical--hidden':
                  expandedSections[section.id],
              }" />
          </div>
          <span class="menu__right__section__header-label">
            {{ section.title }}
          </span>
        </button>

        <div
          :id="`menu-panel-${section.id}`"
          class="menu__right__section__content"
          :class="{
            'menu__right__section__content--expanded': expandedSections[section.id],
          }">
          <nuxt-link
            v-for="(item, index) in section.items"
            :key="item"
            :to="getMenuItemLink(section.id, index)"
            :target="getMenuItemTarget(section.id, index)"
            class="menu__right__section__subtitles-item"
            @click="handleLinkClick">
            {{ item }}
          </nuxt-link>
        </div>
      </div>

      <div class="menu__right__separator" />
    </div>

    <div class="menu__column menu__right menu__right__2">
      <div
        v-for="section in secondColumnSections"
        :key="section.id"
        class="menu__right__section">
        <span
          :id="`menu-section-title-${section.id}`"
          class="menu__right__section__title">
          {{ section.title }}
        </span>

        <button
          :id="`menu-toggle-${section.id}`"
          class="menu__right__section__header"
          type="button"
          :aria-expanded="expandedSections[section.id]"
          :aria-controls="`menu-panel-${section.id}`"
          @click="toggleSection(section.id)">
          <div
            class="menu__right__section__icon"
            aria-hidden="true">
            <span class="menu__right__section__icon-horizontal"></span>
            <span
              class="menu__right__section__icon-vertical"
              :class="{
                'menu__right__section__icon-vertical--hidden':
                  expandedSections[section.id],
              }" />
          </div>
          <span class="menu__right__section__header-label">
            {{ section.title }}
          </span>
        </button>

        <div
          :id="`menu-panel-${section.id}`"
          class="menu__right__section__content"
          :class="{
            'menu__right__section__content--expanded': expandedSections[section.id],
          }">
          <nuxt-link
            v-for="(item, index) in section.items"
            :key="item"
            :to="getMenuItemLink(section.id, index)"
            :target="getMenuItemTarget(section.id, index)"
            class="menu__right__section__subtitles-item"
            @click="handleLinkClick">
            {{ item }}
          </nuxt-link>
        </div>
      </div>

      <div
        class="menu__right__languages"
        role="navigation"
        aria-label="Language selection">
        <nuxt-link
          v-for="lang in languageLinks"
          :key="`desktop-${lang.code}-${locale}-${route.path}`"
          :to="lang.path || '#'"
          :class="{ menu__right__languages__active: lang.isActive }"
          :aria-current="lang.isActive ? 'page' : undefined"
          @click="(event) => handleLocaleLinkClick(event, lang.code)">
          {{ lang.code?.toUpperCase() }}
        </nuxt-link>
      </div>

      <nuxt-link
        :to="localePath({ name: 'core-values' })"
        class="menu__right__green"
        @click="handleLinkClick">
        {{ $t("general.coreValues") }}
      </nuxt-link>
    </div>
  </div>
</template>

<script setup>
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch
  } from 'vue'

  import MenuCard from './MenuCard.vue'

  const {
    t, locale, locales
  } = useI18n()
  const switchLocalePath = useSwitchLocalePath()
  const localePath = useLocalePath()
  const route = useRoute()
  const emit = defineEmits([ 'close-menu' ])

  // Helper function to create locale-aware paths that work in new tabs
  const getLocalizedPath = path => {
    const currentLocale = locale.value || 'it'
    const localePrefix = currentLocale.split('_')[0] // Convert 'it' to 'it'
    return `/${localePrefix}${path}`
  }

  const availableLocales = computed(() => {
    if (!locales) {
      return []
    }

    if (Array.isArray(locales)) {
      return locales?.filter(l => !l.hidden)
    }

    if (Array.isArray(locales.value)) {
      return locales.value?.filter(l => !l.hidden)
    }

  // computed(() => );

  // return [
  // t("menu.languages.it"),
  // t("menu.languages.en"),
  // t("menu.languages.fr"),
  // t("menu.languages.es"),
  // ];
  })

  // Helper function to normalize locale codes for comparison
  const normalizeLocaleCode = value => {
    if (!value) {
      return ''
    }

    return value.toString().toLowerCase().replace('-', '_')
  }

  // Check if a locale is currently active
  const isLocaleActive = code => {
    const normalizedTarget = normalizeLocaleCode(code)
    const normalizedCurrent = normalizeLocaleCode(locale.value)

    if (!normalizedTarget || !normalizedCurrent) {
      return false
    }

    return (
      normalizedCurrent === normalizedTarget
      || normalizedCurrent.startsWith(`${normalizedTarget}_`)
    )
  }

  // Function to generate language links
  const generateLanguageLinks = () => {
    return availableLocales.value.map(lang => {
      const path = switchLocalePath(lang.code)
      const isActive = isLocaleActive(lang.code)

      return {
        code: lang.code,
        name: lang.name,
        path: path || route.fullPath,
        // `path` e' falsy quando la pagina corrente non esiste in quella lingua
        // (es. un racconto di vita non tradotto/attivo li'): in tal caso la voce
        // non va offerta, coerentemente con lo switcher del footer.
        available: !!path,
        isActive
      }
    }).filter(lang => lang.available || lang.isActive)
  }

  // Reactive ref for language links
  const languageLinks = ref(generateLanguageLinks())

  // Watch for locale and route changes to regenerate links
  watch([ locale, () => route.fullPath ], () => {
    languageLinks.value = generateLanguageLinks()
  }, { immediate: true })

  const rivvenditoriItems = computed(() => [
    t('menu.dealers.italy'),
    t('menu.dealers.abroad'),
    t('menu.dealers.initiatives')
  ])

  const aboutItems = computed(() => [
    t('menu.about.company'),
    t('menu.about.awards'),
    t('menu.about.careers'),
    t('menu.about.certifications')
  ])

  const downloadItems = computed(() => [ t('menu.downloads.catalogues'), t('menu.downloads.manuals') ])

  const comunicazioneItems = computed(() => [
    t('menu.communication.magazine'),
    t('menu.communication.news'),
    t('menu.communication.pressReview')
  ])

  const contattiItems = computed(() => [
    t('menu.contactItems.warranty'),
    t('menu.contactItems.postSaleSupport'),
    t('menu.contactItems.faq'),
    t('menu.contactItems.maintenanceTips')
  ])

  const menuSections = computed(() => [
    {
      id: 'dealers',
      title: t('menu.dealers.title'),
      items: rivvenditoriItems.value
    },
    {
      id: 'about',
      title: t('menu.about.title'),
      items: aboutItems.value
    },
    {
      id: 'downloads',
      title: t('menu.downloads.title'),
      items: downloadItems.value
    },
    {
      id: 'communication',
      title: t('menu.communication.title'),
      items: comunicazioneItems.value
    },
    {
      id: 'contacts',
      title: t('menu.contactItems.title'),
      items: contattiItems.value
    }
  ])

  const firstColumnSections = computed(() => menuSections.value.slice(0, 3))
  const secondColumnSections = computed(() => menuSections.value.slice(3))

  const expandedSections = reactive({
    dealers: false,
    about: false,
    downloads: false,
    communication: false,
    contacts: false
  })

  const ACCORDION_MEDIA_QUERY = '(max-width: 1023px)'
  const isAccordionViewport = ref(false)
  let accordionMediaQuery
  let accordionMediaQueryListener

  const syncAccordionState = matches => {
    isAccordionViewport.value = matches
    Object.keys(expandedSections).forEach(key => {
      expandedSections[key] = matches ? false : true
    })
  }

  onMounted(() => {
    if (typeof window === 'undefined') {
      return
    }

    accordionMediaQuery = window.matchMedia(ACCORDION_MEDIA_QUERY)
    syncAccordionState(accordionMediaQuery.matches)

    accordionMediaQueryListener = event => {
      syncAccordionState(event.matches)
    }

    accordionMediaQuery.addEventListener('change', accordionMediaQueryListener)
  })

  onBeforeUnmount(() => {
    accordionMediaQuery?.removeEventListener('change', accordionMediaQueryListener)
  })

  const toggleSection = sectionId => {
    if (!isAccordionViewport.value) {
      return
    }

    Object.keys(expandedSections).forEach(key => {
      if (key !== sectionId) {
        expandedSections[key] = false
      }
    })

    expandedSections[sectionId] = !expandedSections[sectionId]
  }

  const getMenuItemLink = (sectionId, itemIndex) => {
    // Route names for i18n localePath resolution
    const routeNames = {
      dealers: [
        'store-locator-italy-region-province',
        'store-locator-abroad-continent-country',
        'initiatives'
      ],
      about: [
        'company-about',
        'awards',
        // careers: piattaforma esterna Lavora con noi (vedi fallbackPaths)
        null,
        'certifications'
      ],
      downloads: [ 'catalogues', 'manuals' ],
      communication: [
        'magazine',
        'news',
        'press'
      ],
      contacts: [
        'warranty',
        'after-sales-support',
        'faq',
        'maintenance'
      ]
    }

    // Fallback paths for routes without i18n config
    const fallbackPaths = {
      dealers: [
        null,
        null,
        null
      ],
      about: [
        null,
        null,
        'https://lavoraconnoi.venetacucine.com/',
        null
      ],
      downloads: [ null, null ],
      communication: [
        null,
        null,
        null
      ],
      contacts: [
        null,
        null,
        null,
        null
      ]
    }

    const routeName = routeNames[sectionId]?.[itemIndex]
    const fallbackPath = fallbackPaths[sectionId]?.[itemIndex]

    if (routeName) {
      return localePath(routeName)
    }

    if (fallbackPath) {
      // URL esterni (es. careers → Lavora con noi): niente localizzazione
      if (fallbackPath.startsWith('http')) {
        return fallbackPath
      }
      return getLocalizedPath(fallbackPath)
    }

    return '/'
  }

  // Le voci che puntano a URL esterni si aprono in una nuova scheda
  // (NuxtLink aggiunge da solo rel="noopener" ai link esterni con target).
  const getMenuItemTarget = (sectionId, itemIndex) => {
    const to = getMenuItemLink(sectionId, itemIndex)
    return typeof to === 'string' && to.startsWith('http') ? '_blank' : undefined
  }

  const isModifiedClick = event => {
    if (!event) {
      return false
    }

    return (
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1
    )
  }

  const handleLocaleLinkClick = (event, code) => {
    if (isModifiedClick(event)) {
      return
    }

    if (isLocaleActive(code)) {
      event?.preventDefault?.()
      return
    }
  }

  // Handle link clicks - close menu and scroll to top
  const handleLinkClick = () => {
    // Close menu immediately
    emit('close-menu')

    // Scroll to top on navigation
    if (typeof window !== 'undefined') {
      // Use Lenis scrollTo if available, otherwise fallback to window.scrollTo
      nextTick(() => {
        const lenis = window.lenis
        if (lenis && typeof lenis.scrollTo === 'function') {
          lenis.scrollTo(0, { immediate: true })
        } else {
          window.scrollTo({ top: 0, behavior: 'instant' })
        }
      })
    }
  }

  // Handle MenuCard click - navigate to life-story
  const handleMenuCardClick = () => {
    const router = useRouter()
    handleLinkClick() // Close menu and scroll to top
    router.push(localePath('life-stories'))
  }
</script>

<style lang="scss" scoped>
.menu {
  min-width: 100vw;
  max-width: 100vw;
  min-height: calc(100vh - var(--header-height) + 1px);
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  column-gap: 32px;
  overflow: hidden;
  padding-left: 40px;
  padding-right: 40px;
  background:
    linear-gradient(
      to right,
      var(--color-light-grey) 0%,
      var(--color-light-grey) 35%,
      var(--color-white) 35%
    );

  @include up-to-tablet {
    min-height: auto;
    grid-template-columns: 1fr;
    column-gap: 0;
    padding-left: 0;
    padding-right: 0;
    background: transparent;
    overflow-y: auto;
  }

  &__languages-mobile {
    display: none;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
    justify-content: flex-start;

    @include up-to-tablet {
      display: flex;
      margin-bottom: var(--space-m);
      padding-left: 16px;
      padding-right: 16px;
    }

    a {
      min-width: 24px;
      height: 40px;
      cursor: pointer;
      color: var(--color-black);
      font-size: var(--typography-size-xs-desktop, 16px);
      font-weight: 500;
      text-transform: uppercase;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: currentcolor;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
      }

      &:hover::after {
        opacity: 1;
      }
    }

    &__active {
      font-weight: bold !important;
      text-decoration: underline;
    }
  }

  &__column {
    padding: calc(124px - var(--header-height)) 0 40px;

    @include up-to-tablet {
      padding: 0;
    }

    span {
      color: var(--color-red, #dc0814);
      font-size: var(--typography-size-xs-desktop);
      font-style: normal;
      font-weight: 500;
      line-height: 160%;

      /* 25.6px */
      text-transform: uppercase;

      @include up-to-tablet {
        color: var(--color-black);
      }
    }

    h1 {
      font-size: min(4.5vw, 120px);
      font-style: normal;
      font-weight: 500;
      line-height: 125%;

      /* 75px */
      text-transform: uppercase;
      cursor: pointer;

      @include up-to-tablet {
        font-size: 32px;
        color: var(--color-red);
      }
    }
  }

  &__left {
    background-color: var(--color-light-grey);
    grid-column: 1 / 7;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-right: 40px;

    @include up-to-tablet {
      grid-column: 1;
      padding-left: 16px;
      padding-right: 16px;
      margin-bottom: var(--space-m);
    }

    &__top {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    &__top__titles {
      display: flex;
      flex-direction: column;
      gap: 21px;

      h1 {
        transition: transform 0.3s ease-in-out, color 0.3s ease-in-out;
      }

      h1:hover {
        transform: translateX(20px);
        color: var(--color-red, #dc0814);
      }

      @include up-to-tablet {
        gap: var(--space-s);
        margin-bottom: var(--space-m);

        h1 {
          font-size: var(--typography-size-l, 24px);
        }

        h1:hover {
          transform: none;
        }
      }
    }
  }

  &__right {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--spacers-space-xxl, 80px);
    padding-left: 16px;
    padding-right: 16px;

    @include up-to-tablet {
      background-color: white;
      gap: 0;
    }

    &__1 {
      grid-column: 7 / 10;

      @include up-to-tablet {
        padding-top: var(--space-l);
        grid-column: 1;
      }
    }

    &__2 {
      grid-column: 10 / 13;

      @include up-to-tablet {
        grid-column: 1;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: var(--space-kilo);
      }
    }

    &__separator {
      position: absolute;
      height: calc(100% - (124px - var(--header-height)) - 40px);
      right: -16px;
      width: 1px;
      background-color: var(--color-grey);

      @include up-to-tablet {
        display: none;
      }
    }

    &__section {
      display: flex;
      flex-direction: column;
      gap: var(--spacers-space-xs, 16px);

      &__title {
        color: var(--color-red, #dc0814);
        font-size: var(--typography-size-xs-desktop);
        font-style: normal;
        font-weight: 500;
        line-height: 160%;
        text-transform: uppercase;
      }

      &__header {
        display: none;
        align-items: center;
        justify-content: flex-start;
        gap: 16px;
        width: 100%;
        padding: 12px 0;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--color-red, #dc0814);
        font-size: var(--typography-size-xs-desktop);
        font-style: normal;
        font-weight: 500;
        line-height: 160%;
        text-transform: uppercase;
        text-align: left;
      }

      &__header-label {
        font-size: var(--typography-size-xs-desktop);
        font-weight: 500;
        line-height: 160%;
        text-transform: uppercase;
        color: var(--color-red, #dc0814);
      }

      &__icon {
        position: relative;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        &-horizontal,
        &-vertical {
          position: absolute;
          background-color: var(--color-red, #dc0814);
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        &-horizontal {
          width: 12px;
          height: 2px;
        }

        &-vertical {
          width: 2px;
          height: 12px;

          &--hidden {
            transform: rotate(90deg);
            opacity: 0;
          }
        }
      }

      &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacers-space-xs, 16px);
      }

      &__subtitles-item {
        color: #000;
        text-transform: none;
        cursor: pointer;
        position: relative;
        align-self: flex-start;

        &::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 1px;
          background-color: currentcolor;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }

        &:hover::after {
          opacity: 1;
        }

        &.router-link-exact-active {
          text-decoration: underline;
        }
      }

      @include up-to-tablet {
        gap: 0;
        border-bottom: 1px solid var(--color-grey);
        padding: 0;

        &__title {
          display: none;
        }

        &__header {
          display: flex;
          padding: var(--space-s) 0;
          gap: var(--space-s);
        }

        &__content {
          max-height: 0;
          opacity: 0;
          padding: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease;
          border-top: 1px solid transparent;

          // margin-bottom: var(--space-xs);
          padding-left: 36px;
        }

        &__content--expanded {
          max-height: 400px;
          opacity: 1;
          padding: var(--space-xs) 0 var(--space-s);
          padding-left: 36px;
          padding-top: 0;
        }
      }
    }

    &__languages {
      position: absolute;
      bottom: 16px;
      color: var(--color-grey);
      display: flex;
      gap: 23px;
      margin-bottom: 24px;
      flex-wrap: wrap;

      @include up-to-tablet {
        display: none;
      }

      &__active {
        font-weight: bold !important;
        text-decoration: underline;
      }

      a {
        cursor: pointer;
        color: var(--color-black);
        font-size: var(--typography-size-xs-desktop, 16px);
        font-weight: 500;
        text-transform: uppercase;
        position: relative;

        &::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 1px;
          background-color: currentcolor;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }

        &:hover::after {
          opacity: 1;
        }
      }
    }

    &__green {
      display: none;

      @include up-to-tablet {
        display: inline-flex;
        align-items: center;
        color: var(--color-green, #0b8a3c);
        font-size: var(--typography-size-xs-desktop, 16px);
        font-weight: 500;
        text-transform: uppercase;
        text-decoration: none;
        margin-top: var(--space-s);
        margin-bottom: var(--space-s);
        position: relative;
        padding-bottom: var(--space-s);
        border-bottom: 1px solid var(--color-grey);
      }
    }
  }
}
</style>
